import { ReactNode, useEffect, useRef } from "react";

import {
  InteractionStatus,
  AuthenticationResult,
  EventMessage,
  EventType,
  InteractionType,
} from "@azure/msal-browser";
import { MsalAuthenticationTemplate, useMsal } from "@azure/msal-react";
import { loginRequest, msalInstance } from "@src/auth";
import { Loader } from "@src/loader";
import { isTokenExpired } from "@src/components/login/validate_auth_utils";

type Props = {
  children: ReactNode;
};

// (Refactor) Should be replaced with your loading indicator of choice
const LoadingComponent = () => <Loader />;
const ErrorComponent = () => <p>Failed to authenticate...</p>;

export const ValidateAuth = ({ children }: Props) => {
  const { accounts, inProgress } = useMsal();

  const isRefreshing = useRef(false);

  useEffect(() => {
    if (accounts.length > 0) {
      msalInstance.setActiveAccount(accounts[0]);
      if (!isRefreshing.current) {
        isRefreshing.current = true;
        refreshTokenIfExpired(accounts[0]).finally(() => {
          isRefreshing.current = false;
        });
      }
    }

    // Optional - This will update account state if a user signs in from another tab or window
    msalInstance.enableAccountStorageEvents();

    const callbackId = msalInstance.addEventCallback((e: EventMessage) => {
      if (
        [EventType.LOGIN_SUCCESS, EventType.ACQUIRE_TOKEN_SUCCESS].includes(
          e.eventType
        ) &&
        e.payload
      ) {
        const payload = e.payload as AuthenticationResult;
        localStorage.setItem("token", payload.accessToken);
        const account = payload.account;
        msalInstance.setActiveAccount(account);
      }
    });

    return () => {
      if (callbackId) {
        msalInstance.removeEventCallback(callbackId);
      }
    };
  }, [accounts]);

  /**
   * Checks whether the stored access token is expired.
   * If expired and an account exists, attempts a silent refresh.
   * Falls back to redirect authentication if silent refresh fails.
   *
   * @param account The MSAL account object used for acquiring tokens
   */
  async function refreshTokenIfExpired(account: any) {
    const token = localStorage.getItem("token");
    if (!token) return;

    if (isTokenExpired(token) && account) {
      try {
        await msalInstance.acquireTokenSilent({
          account,
          ...loginRequest,
          forceRefresh: true,
        });
      } catch (e) {
        await msalInstance.acquireTokenRedirect({
          account,
          ...loginRequest,
        });
      }
    }
  }

  const inProgressStates = [
    InteractionStatus.Startup,
    InteractionStatus.Login,
    InteractionStatus.HandleRedirect,
  ];

  if (inProgressStates.includes(inProgress)) {
    return <Loader />;
  }

  return (
    <MsalAuthenticationTemplate
      interactionType={InteractionType.Redirect}
      loadingComponent={LoadingComponent}
      errorComponent={ErrorComponent}
    >
      {children}
    </MsalAuthenticationTemplate>
  );
};
